import { inject } from '@angular/core';
import {
  ApiService,
  DocumentType,
  GetBulkPdfDownloadDto,
  GetBulkPdfDownloadSuccess,
  StartBulkDocumentDownloadBody,
  SurveyMarkReportPdfInput,
  SurveyMarkSketchesPdfInput,
  SurveyPlanPdfInput,
} from '@core/api/angular';
import { getBulkPdfDownloadState } from '@core/api/angular/operations/get-bulk-pdf-download-state';
import { GetSurveyMarksListDetails$Params } from '@core/api/angular/operations/get-survey-marks-list-details';
import { startBulkPdfDownload } from '@core/api/angular/operations/start-bulk-pdf-download';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import {
  catchError,
  delayWhen,
  EMPTY,
  expand,
  filter,
  finalize,
  map,
  pipe,
  switchMap,
  take,
  tap,
  timer,
} from 'rxjs';
import { IBulkDownloadableFeature } from './feature.model';
import { ToastService } from './toast/toast.service';

interface BulkDownloadState {
  isLoading: boolean;
}

export type SurveyMarkBulkDownloadReportFormat = Exclude<
  DocumentType,
  DocumentType.HISTORICAL_INFORMATION | DocumentType.SURVEY_PLAN
>;

export function createExportsBulkDownloadPayload(
  items: IBulkDownloadableFeature[],
): StartBulkDocumentDownloadBody['payload'] {
  return items
    .map((item) => {
      if (item.type === 'surveyLabel') {
        return {
          type: DocumentType.SURVEY_PLAN,
          props: {
            surveyLabel: item.displayName,
            prefix: '', // The prefix will be extracted in the backend based on the survey label
          },
        } as SurveyPlanPdfInput;
      } else if (item.type === 'surveyMark') {
        const markReport: SurveyMarkReportPdfInput = {
          type: DocumentType.SURVEY_MARK_REPORT,
          props: {
            markId: parseInt(item.id),
            horizontalDatum: 'GDA2020',
            verticalDatum: 'AHD',
          },
        };

        const markSketches: SurveyMarkSketchesPdfInput = {
          type: DocumentType.SURVEY_MARK_SKETCHES,
          props: {
            markId: item.id.toString(),
            datum: 'GDA2020',
          },
        };

        return [markReport, markSketches];
      }

      throw new Error(`Unknown bulk download item type ${item.type}`);
    })
    .flat();
}

export function createSurveyMarkBulkDownloadPayload(
  type: SurveyMarkBulkDownloadReportFormat,
  options: GetSurveyMarksListDetails$Params,
): StartBulkDocumentDownloadBody['payload'] {
  switch (type) {
    case DocumentType.SURVEY_MARK_HISTORY:
      return options.survey_mark_ids.map((markId) => ({
        type: DocumentType.SURVEY_MARK_HISTORY,
        props: {
          markId: markId.toString(),
          zone: options.zone,
        },
      }));

    case DocumentType.SURVEY_MARK_REPORT:
      return options.survey_mark_ids.map((markId) => ({
        type: DocumentType.SURVEY_MARK_REPORT,
        props: {
          markId,
          zone: options.zone,
          horizontalDatum: options.horizontal_datum,
          verticalDatum: options.vertical_datum,
          atrf2014Epoch: options.atrf2014_epoch,
        },
      }));

    case DocumentType.SURVEY_MARK_SKETCHES:
      return options.survey_mark_ids.map((markId) => ({
        type: DocumentType.SURVEY_MARK_SKETCHES,
        props: {
          markId: markId.toString(),
          datum: options.horizontal_datum,
        },
      }));

    case DocumentType.SURVEY_MARK_PHOTOGRAPHS:
      return options.survey_mark_ids.map((markId) => ({
        type: DocumentType.SURVEY_MARK_PHOTOGRAPHS,
        props: {
          markId: markId.toString(),
          verticalDatum: options.vertical_datum,
        },
      }));
    default:
      throw new Error(`Unsupported document type: ${type}`);
  }
}

export interface IbulkDownloadSuccessOptions {
  url: string;
  successIds: string[];
}

export const BulkDownloadStore = signalStore(
  {
    providedIn: 'root',
  },
  withState<BulkDownloadState>({
    isLoading: false,
  }),
  withMethods(
    (store, apiService = inject(ApiService), toast = inject(ToastService)) => ({
      startBulkDownload: rxMethod<{
        body: StartBulkDocumentDownloadBody;
        onSuccess?: (options: IbulkDownloadSuccessOptions) => void;
        onError?: (error: string) => void;
      }>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          switchMap(({ body, onSuccess, onError }) =>
            apiService.invoke(startBulkPdfDownload, { body }).pipe(
              tap(() => {
                toast.showSuccessMessage(
                  "The file will be downloaded when it's ready",
                );
              }),
              expand(({ execution_id }) =>
                apiService
                  .invoke(getBulkPdfDownloadState, {
                    executionId: execution_id,
                  })
                  .pipe(
                    map(({ result }) => ({ ...result, execution_id })),
                    delayWhen((result) =>
                      timer(result.status === 'pending' ? 1000 : 0),
                    ),
                  ),
              ),
              filter((result) => isSuccess(result)),
              take(1),
              tap((result) => {
                onSuccess?.({
                  url: result.data.url,
                  successIds: result.data.success_ids,
                });
              }),
              catchError((e) => {
                console.error(e);
                onError?.(e);
                toast.showErrorMessage('Error while downloading file');
                return EMPTY;
              }),
              finalize(() => patchState(store, { isLoading: false })),
            ),
          ),
        ),
      ),
    }),
  ),
);

function isSuccess(
  result: GetBulkPdfDownloadDto['result'],
): result is GetBulkPdfDownloadSuccess {
  return result.status === 'success';
}
